// Card 46, range rings (R548; ui-map-interface.md §17.3.46).
//
// A plot draws range rings round own ship at round distances, so a range can
// be read by eye. The ring of this map is the hex ring — the game's own unit
// of distance (CONTEXT.md §2) — so the piece draws hexes and not circles,
// and "two rings to the contact" is the figure the reader sees.
//
// Two things carry rings, and they are not the same rings:
//
//   - a settled BEACON gets its REAL reach. `beam-halo.ts` walks the water
//     hop by hop, exactly as `sim.beamHalo` does on the server, so a ridge
//     stops the ring and the outline bends round the rock. The cumulative
//     set of every hop up to k is one shape, traced by `unionOutline`.
//   - a named own BEACON HULL that has not settled gets three plain hex
//     rings at a fixed radius. Nothing walks there: those rings are a rule
//     of thumb, and the card drew them as plain hexagons for that reason.
//     Any other class gets no ring at all: the owner ruled on 2026-09-05
//     that the rings are for Beacon ships only (R565).
//
// **HOVER OR SELECTION ONLY**, the same ruling card 34 keeps. `show` in the
// TOML carries it, and `"always"` draws one set per settled Beacon.

import { CHART } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { haloCells } from '../../../render/beam-halo';
import { toRoman } from '../../../render/roman';
import {
  COL_PITCH, COLS, HEX_SIZE, ROWS, cellAt, hexCorner,
} from '../../../world';
import type { FrameInfo } from '../../layer';
import type {
  ChartContext, ChartPack, ChartSinks, ChartTier, Piece, RebuildReason,
} from '../context';
import { ShipClass } from '../../../gen/proto/aquaphobia';
import { anchorRingsOf, namedBeacon, namedFleet, namedOwnHull, settledBeacons } from './beacon-read';
import {
  CHART_INK, dashedStrokes, faceOf, outlineStrokes, showsAlways, type Stroke,
} from './shared';

/** Does the shipped table draw every settled Beacon, or only the named one? */
const RINGS_ALWAYS = showsAlways(CHART.pieces.range_rings.show);

/** One ring: its outline, its figure, and where the figure sits. */
interface Ring {
  /** The hop count this ring stands at, 1 upward. */
  k: number;
  strokes: Stroke[];
  /** Where the figure goes: the top of the ring, in world px. */
  lx: number;
  ly: number;
  /** The px figure the modern reading writes at ring 1. */
  px: number;
}

export class RangeRingsPiece implements Piece {
  readonly id = 'range_rings' as const;
  readonly tier: ChartTier = 'screen';
  readonly pack: ChartPack = 'static';
  readonly film = 'hide' as const;
  readonly rebuildOn: readonly RebuildReason[];

  private rings: Ring[] = [];

  /**
   * The same `show` flag card 34 keeps, and for the same reason: the owner
   * may still ask for the rings on the resting map, and the test states
   * both ways without a second table.
   */
  constructor(private readonly always: boolean = RINGS_ALWAYS) {
    this.rebuildOn = always
      ? ['hover', 'selection', 'state', 'hello', 'reading']
      : ['hover', 'selection', 'reading'];
  }

  /** How many rings stand now. The piece's test reads it. */
  get count(): number { return this.rings.length; }

  visible(ctx: ChartContext): boolean {
    return this.always || namedFleet(ctx) !== 0;
  }

  rebuild(ctx: ChartContext): void {
    this.rings = [];
    const t = ctx.cfg.pieces.range_rings;
    const rings = anchorRingsOf(ctx);
    const named = namedBeacon(ctx);
    const beacons = this.always ? settledBeacons(ctx.scene) : named ? [named] : [];
    for (const b of beacons) this.beaconRings(ctx, b.x, b.y, rings, t.fig.label_dx, t.fig.label_dy);
    if (this.always) return;
    // A Beacon hull the commander named, and did not settle, keeps the
    // plain rings: the reach of a hull is not a walk through the water. No
    // other class carries a ring (R565).
    if (beacons.length > 0) return;
    const hull = namedOwnHull(ctx);
    if (!hull || hull.cls !== ShipClass.SHIP_CLASS_BEACON) return;
    for (let k = 1; k <= t.fig.hull_rings; k++) {
      const r = t.fig.hull_ring_base + k * t.fig.hull_ring_step;
      this.rings.push({
        k,
        strokes: hexOutline(hull.x, hull.y, r),
        lx: hull.x + t.fig.label_dx,
        ly: hull.y - r + t.fig.label_dy,
        px: t.fig.hull_ring_step,
      });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    if (this.rings.length === 0) return;
    const t = ctx.cfg.pieces.range_rings;
    const reading = ctx.reading;
    const r = t[reading];
    const face = faceOf(reading);
    // The chrome text weight: the ring line and its figure.
    const pale = CHART_INK.pale;
    for (const ring of this.rings) {
      const alpha = Math.max(0, t.fig.alpha0 - ring.k * t.fig.alpha_step);
      dashedStrokes(sinks.segments, ring.strokes, t.fig.width, pale, alpha, r.dash);
      const text = reading === 'modern'
        ? (ring.k === 1 ? S.rings_modern_one({ px: Math.round(ring.px) }) : S.rings_modern({ k: ring.k }))
        : (ring.k === 1 ? S.rings_historical_one : S.rings_historical({ k: toRoman(ring.k) }));
      ctx.glyphs.word(
        sinks.quads, text, ring.lx, ring.ly,
        face, t.text.main, pale, r.alpha,
      );
    }
  }

  /** The real reach of one Beacon, one cumulative shape per hop. */
  private beaconRings(
    ctx: ChartContext, x: number, y: number, rings: number,
    labelDx: number, labelDy: number,
  ): void {
    const blocked = ctx.scene.blocked;
    if (!blocked) return;
    const origin = cellAt(x, y);
    if (origin < 0) return;
    const walk = haloCells(origin, rings, blocked, COLS, ROWS);
    const upTo: number[] = [];
    let next = 0;
    for (let k = 1; k <= rings; k++) {
      while (next < walk.length && walk[next].ring <= k) upTo.push(walk[next++].cell);
      if (upTo.length === 0) continue;
      const strokes = outlineStrokes(upTo);
      if (strokes.length === 0) continue;
      // Every corner of a closed loop starts one run, so the runs' starts
      // hold every corner.
      let top = Infinity;
      for (const s of strokes) if (s.y0 < top) top = s.y0;
      this.rings.push({
        k, strokes, lx: x + labelDx, ly: top + labelDy, px: COL_PITCH,
      });
    }
  }
}

/** A hexagon of circumradius `r` about a point, as its six sides. */
function hexOutline(cx: number, cy: number, r: number): Stroke[] {
  const k = r / HEX_SIZE;
  const out: Stroke[] = [];
  for (let i = 0; i < 6; i++) {
    const [ax, ay] = hexCorner(i);
    const [bx, by] = hexCorner((i + 1) % 6);
    out.push({
      x0: cx + ax * k, y0: cy + ay * k,
      x1: cx + bx * k, y1: cy + by * k,
    });
  }
  return out;
}
